import React, { useMemo } from 'react';
import { useModalStore } from '@/store';
import ChatInputBanner, { ChatInputBannerAction } from '@/components/ChatInput/ChatInputBanner';
import useResponsiveness from '@/hooks/useResponsiveness';
import {
  trackConversionEvent,
  setCheckoutSourceDimension,
  CONVERSION_ACTION,
} from '@/constants/utils/analyticsEvents';
import useTranslation from '@/hooks/useTranslation';

const BANNER_SURFACE = 'chat_input';

function ChatInputUpgradeBanner({ level, isCloseable = false, isDetached = false }) {
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();

  // Winner of upgrade_banner_copy was "control" (random rotation from
  // existing copy set). The experiment is concluded — see
  // backend/scripts/seed/seed_real_experiments.json.
  const ctaTitles = useMemo(() => ({
    isXS: t('chatInput.upgradeBanner.isXS'),
    default: t('chatInput.upgradeBanner.default'),
  }), [t]);

  const titleRef = React.useRef(null);
  if (titleRef.current === null) {
    const variants = ctaTitles[isXS ? 'isXS' : 'default'];
    const idx = Math.floor(Math.random() * variants.length);
    titleRef.current = variants[idx];
  }
  const title = titleRef.current;
  const linkText = t('chatInput.upgradeBanner.upgradeLink');

  // The click was already tracked; without the matching impression there is no
  // denominator, so the banner's click-through rate can't be computed and one
  // placement can't be compared against another.
  React.useEffect(() => {
    trackConversionEvent(CONVERSION_ACTION.UPGRADE_BANNER_SHOWN, BANNER_SURFACE);
  }, []);

  const handleUpgradePress = React.useCallback(() => {
    trackConversionEvent(CONVERSION_ACTION.UPGRADE_BANNER_CLICKED, BANNER_SURFACE);
    setCheckoutSourceDimension(BANNER_SURFACE);
    openModal('PricingModal');
  }, [openModal]);

  return (
    <ChatInputBanner color="upgrade" level={level} isCloseable={isCloseable} isDetached={isDetached}
      actions={<ChatInputBannerAction onPress={handleUpgradePress}>{linkText}</ChatInputBannerAction>}>
      {title}
    </ChatInputBanner>
  );
}

export default React.memo(ChatInputUpgradeBanner); 